import { Select as ArkSelect, createListCollection } from '@ark-ui/react/select';
import { ChevronDown, Check } from 'lucide-react';
import { forwardRef, useMemo } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ options, value, onChange, placeholder = 'Pilih...', label, error, disabled, className = '' }, ref) => {
    const collection = useMemo(
      () => createListCollection({ items: options }),
      [options]
    );

    return (
      <div className={`w-full ${className}`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <ArkSelect.Root
          collection={collection}
          value={value ? [value] : []}
          onValueChange={(e) => {
            if (e.value && e.value.length > 0) {
              onChange?.(e.value[0]);
            }
          }}
          disabled={disabled}
        >
          <ArkSelect.Control ref={ref}>
            <ArkSelect.Trigger
              className={`
                w-full px-3 py-2 text-base rounded-lg border
                bg-white text-left flex items-center justify-between
                transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                disabled:bg-gray-100 disabled:cursor-not-allowed
                ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'}
              `}
            >
              <ArkSelect.ValueText placeholder={placeholder} />
              <ArkSelect.Indicator>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </ArkSelect.Indicator>
            </ArkSelect.Trigger>
          </ArkSelect.Control>
          <ArkSelect.Positioner>
            <ArkSelect.Content className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden z-50">
              {options.map((option) => (
                <ArkSelect.Item
                  key={option.value}
                  item={option}
                  className={`
                    px-3 py-2 cursor-pointer flex items-center justify-between
                    hover:bg-gray-100 data-[highlighted]:bg-gray-100
                    data-[state=checked]:bg-primary-50
                  `}
                >
                  <ArkSelect.ItemText>{option.label}</ArkSelect.ItemText>
                  <ArkSelect.ItemIndicator>
                    <Check className="w-4 h-4 text-primary-600" />
                  </ArkSelect.ItemIndicator>
                </ArkSelect.Item>
              ))}
            </ArkSelect.Content>
          </ArkSelect.Positioner>
        </ArkSelect.Root>
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';
